import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  ArrowLeft,
  Ellipsis,
  MessageCircle,
  SlidersHorizontal,
  Loader2,
} from "lucide-react";

import "./DogSharingJourneyPage.css";

import dogHeadLevel0 from "../../assets/dogLevel1.png";
import dogHeadLevel1 from "../../assets/dogLevel2.png";
import dogHeadLevel2 from "../../assets/dogLevel3.png";
import dogHeadLevel3 from "../../assets/dogLevel4.png";
import dogPlaceholder from "../../assets/dogPlaceholder.png";
import userPlaceholder from "../../assets/userPlaceholder.png";

import TopNavBar from "../../components/TopNavBar";
import appIcon from "../../assets/socialPawsLogo.svg";
import MobileTopBar from "../../components/MobileTopBar";
import apiClient from "../../api/apiClient";

const DogSharingJourneyPage = () => {
  const navigate = useNavigate();
  const { profile } = useSelector((state) => state.user);

  const dogIcons = [dogHeadLevel0, dogHeadLevel1, dogHeadLevel2, dogHeadLevel3];

  const [journeyData, setJourneyData] = useState([]);
  const [selectedMobileUserId, setSelectedMobileUserId] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [userDogStatus, setUserDogStatus] = useState(0);

  // Fetch from backend
  useEffect(() => {
    const fetchJourneyData = async () => {
      if (!profile?.id) return;

      setIsLoading(true);
      try {
        const userRes = await apiClient.get(`/user/details/${profile.id}`);
        if (userRes.data?.status && userRes.data?.data) {
          setUserDogStatus(userRes.data.data.user_dog_status || 0);
        }

        const response = await apiClient.get(
          `/milestone/get-DogSharing-Journey-By-UserId/${profile.id}`,
        );

        if (response.data?.status && response.data?.data) {
          // Sort by highest milestone, then by total bookings
          const sortedData = response.data.data
            .sort((a, b) => {
              if (b.milestone !== a.milestone) {
                return (b.milestone || 0) - (a.milestone || 0);
              }
              return (b.total_booking || 0) - (a.total_booking || 0);
            })
            .slice(0, 4); // Take top 4

          const mappedData = sortedData.map((item) => {
            const milestoneVal = item.milestone || 0;
            let progress = 0;
            let showSlider = true;

            // Map API milestone values to the 4-step visual slider (index 0 to 3)
            if (milestoneVal === 0) {
              showSlider = false;
              progress = 0; // Default fallback for array indexing
            } else if (milestoneVal === 0.5) {
              progress = 0; // TOSS TREATS (Step 1)
            } else if (milestoneVal === 1) {
              progress = 1; // MILESTONE 1 (Step 2)
            } else if (milestoneVal === 2) {
              progress = 2; // MILESTONE 2 (Step 3)
            } else if (milestoneVal >= 3) {
              progress = 3; // MILESTONE 3 (Step 4)
            }

            return {
              id: item.partner_user_id,
              name: item.partner_name || `User ${item.partner_user_id}`,
              avatar: item.profile_pic?.[0] || null,
              progress,
              showSlider,
            };
          });

          setJourneyData(mappedData);
          if (mappedData.length > 0) {
            setSelectedMobileUserId(mappedData[0].id);
          }
        }
      } catch (error) {
        console.error("Error fetching dog sharing journey:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchJourneyData();
  }, [profile?.id]);

  const milestones = [
    {
      label: "TOSS TREATS",
      desc: "Show interest in the neighborhood pups you want to meet",
    },
    {
      label: "MILESTONE 1",
      title: "GROUP WALK",
      desc: "First Meetup Walk You, the Pup, and Pup Pawrent",
    },
    {
      label: "MILESTONE 2",
      title: userDogStatus === 0 ? "TAKE THE LEASH" : "PASS THE LEASH",
      desc: "Take solo-time with the pup, either on a walk or play-over",
    },
    {
      label: "MILESTONE 3",
      title: "TRUSTED PUP PAL",
      desc: "Trust has been established to book walks, play-overs, and stayovers with this pup!",
    },
  ];

  const TRACK_START = 5;
  const TRACK_END = 95;
  const TRACK_RANGE = TRACK_END - TRACK_START;

  const MOBILE_TRACK_START = 10;
  const MOBILE_TRACK_END = 90;
  const MOBILE_TRACK_RANGE = MOBILE_TRACK_END - MOBILE_TRACK_START;

  const handleImgError = (e) => {
    e.target.src = dogPlaceholder;
  };

  const selectedMobileUser =
    journeyData.find((u) => u.id === selectedMobileUserId) || journeyData[0];

  return (
    <div className="journey-container">
      {/* TOP NAV */}
      <div className="cb-custom-top-header cb-desktop-only">
        <div
          className="cb-custom-brand"
          onClick={() => navigate("/dashboard-switcher")}
        >
          <img
            src={appIcon}
            alt="Social Paws"
            style={{ width: "32px", height: "32px", objectFit: "contain" }}
          />
          <span>Social Paws</span>
        </div>
        <div className="header-icons-right">
          <TopNavBar theme="light" />
        </div>
      </div>
      <div
        className="cb-custom-top-header cb-mobile-only"
        style={{ background: "transparent" }}
      >
        {/* TOP ROW */}
        <div className="cb-header-top-row">
          <MobileTopBar
            onBack={() => navigate("/dashboard-switcher")}
            backColor="#000"
            logoColor="#009fd9"
            blueLogo={true}
          />
        </div>

        {/* BOTTOM ROW */}
        <div className="cb-header-bottom-row">
          <div className="header-icons-right">
            <TopNavBar theme="light" />
          </div>
        </div>
      </div>

      <h1 className="journey-title">YOUR DOG-SHARING JOURNEY</h1>

      {isLoading ? (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            flex: 1,
          }}
        >
          <Loader2 className="animate-spin" size={40} color="#000" />
        </div>
      ) : journeyData.length === 0 ? (
        <div
          style={{
            textAlign: "center",
            marginTop: "2rem",
            fontWeight: "600",
            color: "rgba(0,0,0,0.6)",
          }}
        >
          <p>No journey milestones found.</p>
        </div>
      ) : (
        <>
          {/* DESKTOP/IPAD CONTENT */}
          <div className="journey-desktop-only">
            <div className="journey-header-content">
              <div className="milestone-def-grid">
                {milestones.map((m, i) => (
                  <div key={i} className="milestone-def-item">
                    <h3 className="def-label">{m.label}</h3>
                    {m.title && <h4 className="def-title">{m.title}</h4>}
                    <p className="def-desc">{m.desc}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="journey-list">
              {journeyData.map((user) => {
                const positionPercent =
                  TRACK_START + (user.progress / 3) * TRACK_RANGE;

                return (
                  <div key={user.id} className="journey-row">
                    <div className="journey-user-avatar">
                      <img
                        src={user.avatar || userPlaceholder}
                        alt={user.name}
                        onError={handleImgError}
                      />
                    </div>

                    {user.showSlider ? (
                      <div className="journey-slider-container">
                        <div className="journey-track-bg" />
                        <div
                          className="journey-track-fill"
                          style={{
                            width: `${positionPercent}%`,
                          }}
                        />

                        {[0, 1, 2, 3].map((step) => (
                          <div
                            key={step}
                            className="journey-dot"
                            style={{
                              left: `${TRACK_START + (step / 3) * TRACK_RANGE}%`,
                            }}
                          />
                        ))}

                        <div
                          className="journey-dog-thumb"
                          style={{ left: `${positionPercent}%` }}
                        >
                          <img
                            src={dogIcons[user.progress]}
                            alt="Dog level"
                            style={{
                              width: user.progress >= 2 ? "60px" : "53px",
                              height: user.progress >= 2 ? "60px" : "53px",
                            }}
                          />
                        </div>

                        <div className="journey-labels-row">
                          {[
                            "TOSS TREATS",
                            "MILESTONE 1",
                            "MILESTONE 2",
                            "MILESTONE 3",
                          ].map((label, i) => (
                            <span
                              key={i}
                              className={user.progress === i ? "active" : ""}
                            >
                              {label}
                            </span>
                          ))}
                        </div>
                      </div>
                    ) : (
                      <div
                        className="journey-slider-container"
                        style={{
                          display: "flex",
                          alignItems: "center",
                          paddingLeft: "15px",
                          color: "rgba(0,0,0,0.5)",
                        }}
                      >
                        No milestones reached yet.
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* MOBILE CONTENT */}
          <div className="journey-mobile-wrapper journey-mobile-only">
            {/* Left Side: Users List */}
            <div className="mobile-users-sidebar">
              {journeyData.map((user) => (
                <div
                  className="mobile-user-row"
                  key={user.id}
                  onClick={() => setSelectedMobileUserId(user.id)}
                >
                  <div
                    className={`mobile-radio ${
                      user.id === selectedMobileUserId ? "active" : ""
                    }`}
                  >
                    {user.id === selectedMobileUserId && (
                      <div className="mobile-radio-inner" />
                    )}
                  </div>
                  <div
                    className={`mobile-avatar-wrap ${
                      user.id === selectedMobileUserId ? "active" : ""
                    }`}
                  >
                    <img
                      src={user.avatar || userPlaceholder}
                      alt={user.name}
                      onError={handleImgError}
                    />
                  </div>
                  <div className="mobile-user-status">
                    {!user.showSlider
                      ? "0"
                      : user.progress === 0
                        ? "TT"
                        : `M${user.progress}`}
                  </div>
                </div>
              ))}
            </div>

            <div className="mobile-divider" />

            {/* Middle: Vertical Slider & Right: Texts */}
            <div className="mobile-right-panel">
              {selectedMobileUser.showSlider ? (
                <>
                  <div className="mobile-slider-area">
                    <div className="mobile-track-bg"></div>

                    <div
                      className="mobile-track-fill"
                      style={{
                        height: `${(selectedMobileUser.progress / 3) * MOBILE_TRACK_RANGE}%`,
                      }}
                    ></div>

                    {[0, 1, 2, 3].map((step) => (
                      <div
                        key={step}
                        className="mobile-dot"
                        style={{
                          bottom: `${MOBILE_TRACK_START + (step / 3) * MOBILE_TRACK_RANGE}%`,
                        }}
                      />
                    ))}

                    <div
                      className="mobile-dog-thumb"
                      style={{
                        bottom: `${
                          MOBILE_TRACK_START +
                          (selectedMobileUser.progress / 3) * MOBILE_TRACK_RANGE
                        }%`,
                        left:
                          window.innerWidth <= 1023
                            ? selectedMobileUser.progress <= 1
                              ? "40%"
                              : "33%"
                            : undefined,
                      }}
                    >
                      <img
                        src={dogIcons[selectedMobileUser.progress]}
                        alt="Dog level"
                        style={{
                          width:
                            selectedMobileUser.progress >= 2 ? "70px" : "60px",
                          height:
                            selectedMobileUser.progress >= 2 ? "70px" : "60px",
                        }}
                      />
                    </div>
                  </div>

                  <div className="mobile-milestones-list">
                    {[...milestones].reverse().map((m, i) => {
                      const stepIndex = 3 - i;
                      return (
                        <div
                          key={i}
                          className="mobile-milestone-item"
                          style={{
                            bottom: `${
                              MOBILE_TRACK_START +
                              (stepIndex / 3) * MOBILE_TRACK_RANGE
                            }%`,
                          }}
                        >
                          <h3 className="mobile-m-label">
                            {m.label} <br />
                            {m.title && (
                              <span className="mobile-m-title">{m.title}</span>
                            )}
                          </h3>
                          <p className="mobile-m-desc">{m.desc}</p>
                        </div>
                      );
                    })}
                  </div>
                </>
              ) : (
                <div
                  style={{
                    flex: 1,
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    textAlign: "center",
                    color: "rgba(0,0,0,0.5)",
                    padding: "1rem",
                  }}
                >
                  No milestones reached yet.
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default DogSharingJourneyPage;
